import React from 'react';
import type { SocialIconsComponent } from '../../../types/builder';
import { Instagram, Twitter, Facebook, Youtube, Send, MessageCircle } from 'lucide-react';

interface SocialIconsRendererProps {
  component: SocialIconsComponent;
}

const socialIconComponents = {
  instagram: Instagram,
  twitter: Twitter,
  facebook: Facebook,
  tiktok: null, // Will use custom SVG
  youtube: Youtube,
  telegram: Send,
  whatsapp: MessageCircle,
};

const TikTokIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export const SocialIconsRenderer: React.FC<SocialIconsRendererProps> = ({ component }) => {
  const { icons, iconSize, iconColor, spacing, backgroundColor, alignment, padding } = component.settings;

  const alignmentStyles = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  return (
    <div
      style={{
        padding: `${padding}px`,
        backgroundColor,
        display: 'flex',
        justifyContent: alignmentStyles[alignment],
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: `${spacing}px`,
          flexWrap: 'wrap',
        }}
      >
        {icons.map((icon) => {
          const IconComponent =
            socialIconComponents[icon.platform as keyof typeof socialIconComponents];
          
          return (
            <a
              key={icon.id}
              href={icon.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: `${iconSize}px`,
                height: `${iconSize}px`,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {icon.platform === 'tiktok' ? (
                <TikTokIcon size={iconSize * 0.5} color={iconColor} />
              ) : icon.platform === 'custom' && icon.iconUrl ? (
                <img
                  src={icon.iconUrl}
                  alt="Custom icon"
                  style={{
                    width: `${iconSize * 0.5}px`,
                    height: `${iconSize * 0.5}px`,
                    objectFit: 'contain',
                  }}
                />
              ) : IconComponent ? (
                <IconComponent size={iconSize * 0.5} color={iconColor} />
              ) : null}
            </a>
          );
        })}
      </div>
    </div>
  );
};

